import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { auth, functions } from '../firebaseConfig';
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from 'firebase/functions';
import { useError } from '../contexts/ErrorContext';
import { usePlan } from '../contexts/PlanContext';
import Button from './ui/Button';

const PricingTable = ({ products, onPlanSelect, showComparison = true, currentSubscription }) => {
  const { showError } = useError();
  const { hasSubscription } = usePlan();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setIsAuthenticated(!!currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const formatPrice = (priceInCents) => {
    const price = priceInCents / 100;
    return price.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const isCurrentUserPlan = (product) => {
    if (!currentSubscription?.role) return false;
    return currentSubscription.role === product.metadata?.firebaseRole;
  };

  // Plan selection handler with upgrade support
  const handlePlanSelection = async (product) => {
    if (isCurrentUserPlan(product)) {
      return;
    }

    if (onPlanSelect) {
      onPlanSelect(product);
      return;
    }

    const planData = {
      priceId: product.prices[0]?.id,
      planName: product.name,
      price: product.prices[0]?.unit_amount,
      productId: product.id,
      isFree: product.metadata?.type === 'free' || product.id === 'free'
    };

    if (!isAuthenticated) {
      navigate(`/signup?plan=${encodeURIComponent(JSON.stringify(planData))}`);
      return;
    }

    setLoading(true);
    try {
      if (hasSubscription && !planData.isFree) {
        // UPGRADE: modify existing subscription (no new sub created)
        const upgradeFn = httpsCallable(functions, 'upgrade_subscription');
        await upgradeFn({ priceId: planData.priceId });
        const syncFn = httpsCallable(functions, 'sync_subscription');
        await syncFn();
        await auth.currentUser.getIdToken(true);
        window.location.href = '/profile';
      } else {
        // FIRST SUBSCRIPTION: use Stripe Checkout
        const createCheckoutFn = httpsCallable(functions, 'create_checkout_session');
        const result = await createCheckoutFn({
          priceId: planData.priceId,
          successUrl: window.location.origin + '/success',
          cancelUrl: window.location.origin + '/pricing',
        });
        window.location.assign(result.data.url);
      }
    } catch (error) {
      showError(error);
      setLoading(false);
    }
  };

  const renderPricingCard = (product, index) => {
    const isCurrentPlan = isCurrentUserPlan(product);
    const isFreeProduct = product.metadata?.type === 'free' || product.id === 'free';
    
    let buttonText = 'Começar Agora';
    if (isAuthenticated) {
      if (isCurrentPlan) {
        buttonText = 'Plano Atual';
      } else if (isFreeProduct && hasSubscription) {
        buttonText = 'Plano Indisponível';
      } else {
        buttonText = hasSubscription ? 'Atualizar Plano' : 'Escolher Plano';
      }
    }

    const isDisabled = isCurrentPlan || (isAuthenticated && isFreeProduct && hasSubscription);

    return (
      <motion.div
        key={product.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.2 }}
        className={`relative bg-white rounded-2xl p-8 shadow-lg border group
          ${isCurrentPlan 
            ? 'border-indigo-500 ring-2 ring-indigo-500 ring-opacity-50'
            : product.metadata?.highlighted
              ? 'border-purple-400 ring-2 ring-purple-400 ring-opacity-50 scale-105'
              : 'border-purple-300 hover:border-purple-400 hover:ring-2 hover:ring-purple-400 hover:ring-opacity-50'
          } hover:shadow-xl transition-all duration-300`}
      >
        {isCurrentPlan && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <span className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white px-4 py-1 rounded-full text-sm font-medium">
              Seu Plano Atual
            </span>
          </div>
        )}

        {!isCurrentPlan && product.metadata?.highlighted && (
          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-5 py-1.5 rounded-full text-sm font-semibold shadow-lg whitespace-nowrap">
              Mais Popular
            </span>
          </div>
        )}

        <div className="text-center mb-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">{product.name}</h3>
          <div className="flex items-baseline justify-center gap-2">
            <span className={`text-4xl font-extrabold ${
              isCurrentPlan
                ? 'bg-gradient-to-r from-indigo-500 to-violet-500'
                : 'bg-gradient-to-r from-purple-600 to-pink-600'
            } bg-clip-text text-transparent`}>
              {product.prices && product.prices[0] ? formatPrice(product.prices[0].unit_amount) : 'Indisponível'}
            </span>
            <span className="text-gray-500">/mês</span>
          </div>
        </div>

        <ul className="space-y-4 mb-8 min-h-[240px]">
          {product.features?.map((feature, idx) => (
            <li key={idx} 
                className="flex items-start gap-3 text-gray-600"
            >
              <span className={`mt-1 ${
                isCurrentPlan ? 'text-indigo-500' : 'text-purple-500'
              }`}>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                </svg>
              </span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {!isCurrentPlan ? (
          <Button
            onClick={() => handlePlanSelection(product)}
            disabled={isDisabled || loading}
            loading={loading}
            fullWidth
            size="lg"
          >
            {buttonText}
          </Button>
        ) : (
          <div className="w-full py-3.5 rounded-xl font-semibold text-center text-lg bg-gradient-to-r from-indigo-500 to-violet-500 text-white">
            {buttonText}
          </div>
        )}

        {!isFreeProduct && (
          <p className="text-center text-sm text-gray-500 mt-4">
            Garantia de 7 dias ou seu dinheiro de volta
          </p>
        )}
      </motion.div>
    );
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {products.map((product, index) => renderPricingCard(product, index))}
      </div>

      {showComparison && (
        <div className="mt-16 p-6 bg-white rounded-xl shadow-lg border border-purple-100">
          <h3 className="text-xl font-bold text-center mb-6 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Comparação Detalhada dos Planos
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-purple-100">
                  <th className="px-4 py-3 text-left text-purple-600 font-semibold">Recurso</th>
                  {products.map(product => (
                    <th key={product.id} className="px-4 py-3 text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent font-bold">
                      {product.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Build inherited features: Pro inherits Free, Premium inherits Pro
                  const sorted = [...products].sort((a, b) =>
                    parseInt(a.metadata?.order || '0') - parseInt(b.metadata?.order || '0')
                  );
                  const expandedFeatures = {};
                  let inherited = [];
                  for (const p of sorted) {
                    const own = (p.features || []).filter(f => !f.startsWith('Tudo do'));
                    expandedFeatures[p.id] = [...inherited, ...own];
                    inherited = expandedFeatures[p.id];
                  }
                  const allFeatures = [...new Set(Object.values(expandedFeatures).flat())];

                  return allFeatures.map((feature, idx) => (
                    <tr key={idx} className="border-b border-purple-50 hover:bg-purple-50/30 transition-colors">
                      <td className="px-4 py-3 text-gray-700 font-medium">{feature}</td>
                      {products.map(product => (
                        <td key={`${product.id}-${idx}`} className="px-4 py-3 text-center">
                          {expandedFeatures[product.id]?.includes(feature) ? (
                            <span className="text-purple-600 text-xl">{'\u2713'}</span>
                          ) : (
                            <span className="text-pink-400 text-xl">{'\u00d7'}</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingTable;
